// 一次语音输入：开麦克风、连 lingzhi-studio 的 /studio/api/voice/stream（它再连阿里云），边说边把字填进输入框光标处。
// 连上就发 start（带登录令牌，不放网址里），不等回话就开始送音频（studio 先收着），省掉一来一回的等待。
// 令牌过期：刷新一次令牌、重连，把已经录的音频补送过去，说的话不丢。

import { getSelectedTenantId, getToken } from '../../api/session'
import { refreshSession } from '../../api/client'
import { CaptureError, newAudioContext, startCapture, type Capture } from './capture'

export type DictationState = 'starting' | 'listening' | 'finishing' | 'idle'

export interface DictationEvents {
  onState: (s: DictationState) => void
  /** 输入框里应该是的全文，和光标放在哪。 */
  onText: (value: string, caret: number) => void
  /** 响度 0–1、说了几秒。每 100 毫秒一次。 */
  onLevel: (level: number, seconds: number) => void
  onError: (message: string) => void
  onNote: (message: string) => void
}

export interface DictationDeps {
  connect: (url: string) => WebSocket
  capture: (onChunk: (pcm: ArrayBuffer, rms: number) => void) => Promise<Capture>
  token: () => string | null
  tenant: () => string | null
  refresh: () => Promise<boolean>
  model?: string
  url: string
  /** 多久没听到声音就自己停（毫秒）。 */
  silenceMs: number
}

export function defaultDeps(model?: string): DictationDeps {
  // 先建好 AudioContext：这一步要在点击里同步做（iPhone）
  const ctx = newAudioContext()
  return {
    connect: (url) => new WebSocket(url),
    capture: (onChunk) => startCapture(ctx, onChunk).catch((e) => {
      void ctx.close().catch(() => undefined)
      throw e
    }),
    token: getToken,
    tenant: getSelectedTenantId,
    refresh: refreshSession,
    model,
    url: `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/studio/api/voice/stream`,
    silenceMs: 15_000,
  }
}

/** 说的话放进原来的字中间：英文和英文之间补个空格，中文直接接。 */
export function compose(before: string, spoken: string, after: string): { value: string; caret: number } {
  const latin = (c: string | undefined) => !!c && /[A-Za-z0-9]/.test(c)
  const lead = before && !/\s$/.test(before) && latin(before.at(-1)) && latin(spoken[0]) ? ' ' : ''
  const trail = after && spoken && !/^\s/.test(after) && latin(spoken.at(-1)) && latin(after[0]) ? ' ' : ''
  const head = before + lead + spoken + trail
  return { value: head + after, caret: head.length }
}

const LOUD = 0.015

export class Dictation {
  private ws: WebSocket | null = null
  private capture: Capture | null = null
  private state: DictationState = 'idle'
  private spoken = ''
  /** 收到 ready 之前送的音频：令牌过期要重连时补送。 */
  private unconfirmed: ArrayBuffer[] = []
  private ready = false
  private retried = false
  private bytes = 0
  private lastLoud = Date.now()
  private waiters: ((value: string) => void)[] = []
  private ended = false
  private readonly before: string
  private readonly after: string

  constructor(
    base: { value: string; start: number; end: number },
    private readonly ev: DictationEvents,
    private readonly deps: DictationDeps,
  ) {
    this.before = base.value.slice(0, base.start)
    this.after = base.value.slice(base.end)
  }

  get value() {
    return compose(this.before, this.spoken, this.after).value
  }

  private set(s: DictationState) {
    this.state = s
    this.ev.onState(s)
  }

  async start() {
    this.set('starting')
    this.open()
    try {
      this.capture = await this.deps.capture((pcm, rms) => this.chunk(pcm, rms))
    } catch (e) {
      this.fail(e instanceof CaptureError ? e.message : '打不开麦克风')
      return
    }
    if (this.ended) {
      void this.capture.stop()
      return
    }
    this.lastLoud = Date.now()
    if (this.state === 'starting') this.set('listening')
  }

  private open() {
    const ws = this.deps.connect(this.deps.url)
    ws.binaryType = 'arraybuffer'
    this.ws = ws
    ws.onopen = () => {
      const tenant = this.deps.tenant()
      ws.send(JSON.stringify({ type: 'start', token: this.deps.token() ?? '', ...(tenant ? { tenant } : {}), ...(this.deps.model ? { model: this.deps.model } : {}) }))
      for (const c of this.unconfirmed) ws.send(c)
      if (this.state === 'finishing') ws.send(JSON.stringify({ type: 'stop' }))
    }
    ws.onmessage = (e) => {
      if (this.ws !== ws || typeof e.data !== 'string') return
      let m: { type?: string; text?: string; message?: string; status?: number }
      try {
        m = JSON.parse(e.data)
      } catch {
        return
      }
      if (m.type === 'ready') {
        this.ready = true
        this.unconfirmed = []
      } else if (m.type === 'text') this.show(m.text ?? '')
      else if (m.type === 'limit') {
        this.ev.onNote(m.message ?? '说得太久了，先停一下')
        void this.stop()
      } else if (m.type === 'done') {
        this.show(m.text ?? this.spoken)
        this.end()
      } else if (m.type === 'error') {
        if (m.status === 401 && !this.ready && !this.retried) return void this.retry()
        this.fail(m.message ?? '语音识别出错了')
      }
    }
    ws.onclose = () => {
      if (this.ws !== ws || this.ended) return
      if (this.state === 'finishing') this.end()
      else this.fail('语音识别断开了')
    }
  }

  private async retry() {
    this.retried = true
    const old = this.ws
    this.ws = null
    old?.close()
    if (!(await this.deps.refresh())) return this.fail('登录已过期，请重新登录')
    if (!this.ended) this.open()
  }

  private chunk(pcm: ArrayBuffer, rms: number) {
    if (this.ended) return
    this.bytes += pcm.byteLength
    if (!this.ready) this.unconfirmed.push(pcm)
    // 还没连上的先排着，连上时（onopen）一起送
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(pcm)
    const now = Date.now()
    if (rms > LOUD) this.lastLoud = now
    this.ev.onLevel(Math.min(1, rms * 6), this.bytes / 32000)
    if (this.state === 'listening' && now - this.lastLoud > this.deps.silenceMs) {
      this.ev.onNote('好一会儿没听到声音，先停了')
      void this.stop()
    }
  }

  private show(text: string) {
    this.spoken = text
    const { value, caret } = compose(this.before, text, this.after)
    this.ev.onText(value, caret)
  }

  /** 说完了：关麦克风，等最后几个字（最多 5 秒），返回输入框里的全文。 */
  stop(): Promise<string> {
    if (this.ended) return Promise.resolve(this.value)
    const p = new Promise<string>((r) => this.waiters.push(r))
    if (this.state !== 'finishing') {
      this.set('finishing')
      const cap = this.capture
      this.capture = null
      void (cap ? cap.stop() : Promise.resolve()).then(() => {
        if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'stop' }))
      })
      setTimeout(() => this.end(), 5000)
    }
    return p
  }

  /** 不要了（离开页面）：什么都不等。 */
  cancel() {
    this.end()
  }

  private fail(message: string) {
    if (this.ended) return
    this.ev.onError(message)
    this.end()
  }

  private end() {
    if (this.ended) return
    this.ended = true
    void this.capture?.stop()
    this.capture = null
    const ws = this.ws
    this.ws = null
    if (ws && ws.readyState <= WebSocket.OPEN) ws.close()
    this.set('idle')
    for (const w of this.waiters.splice(0)) w(this.value)
  }
}
